// Public checklist-fill page. Reached from the second email a reporter
// gets after submitting a public site-QR report under a risk category
// that's linked to a published checklist — the email link encodes
// `/checklist-fill/<orgSlug>/<token>`, the token alone being the trust
// boundary (no re-confirming name/email, same model as the report form
// itself trusts the org-slug+site-id pair).
//
// Mirrors PublicReportPage's conventions: plain `fetch`, no auth store,
// no react-query. Three states:
//   • loading / error — boot fetch in flight or failed (invalid/expired link)
//   • form            — answer questions, upload evidence, submit
//   • recap           — read-only view of what was submitted (either
//                       because this token was already used, or because
//                       this visit just used it)

import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Alert, Box, Button, Chip, CircularProgress, Container, IconButton, MenuItem,
  Paper, Rating, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined'
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import type {
  ChecklistItem, ChecklistItemType, ChecklistResponse, ChecklistResponseItem, ChecklistSection,
} from '@cmms/types'

import { baseUrl } from '../../lib/api'

type MediaEntry = { url: string; name: string; size: number; type: string }

type AnswerState = {
  value_text: string | null
  value_number: number | null
  value_options: string[] | null
  notes: string | null
  flagged: boolean
  media_urls: MediaEntry[]
}

const emptyAnswer = (): AnswerState => ({
  value_text: null, value_number: null, value_options: null, notes: null, flagged: false, media_urls: [],
})

function toAnswerState(item?: ChecklistResponseItem): AnswerState {
  if (!item) return emptyAnswer()
  const rawMedia = (item as any).media_urls
  const media = Array.isArray(rawMedia) ? rawMedia : (typeof rawMedia === 'string' ? JSON.parse(rawMedia || '[]') : [])
  return {
    value_text: item.value_text ?? null,
    value_number: item.value_number ?? null,
    value_options: Array.isArray(item.value_options) ? item.value_options : null,
    notes: item.notes ?? null,
    flagged: item.flagged ?? false,
    media_urls: media,
  }
}

export function PublicChecklistFillPage() {
  const { orgSlug = '', token = '' } = useParams<{ orgSlug: string; token: string }>()

  const [boot, setBoot] = useState<ChecklistResponse | null>(null)
  const [alreadySubmitted, setAlreadySubmitted] = useState(false)
  const [bootErr, setBootErr] = useState<{ status: number; message: string } | null>(null)

  const [answers, setAnswers] = useState<Record<string, AnswerState>>({})
  const [unmet, setUnmet] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitErr, setSubmitErr] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    let live = true
    ;(async () => {
      try {
        const res = await fetch(`${baseUrl}/api/v1/public/checklist-fill/${encodeURIComponent(orgSlug)}/${encodeURIComponent(token)}`)
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw Object.assign(new Error(data.message ?? `HTTP ${res.status}`), { status: res.status })
        if (!live) return
        setBoot(data.response as ChecklistResponse)
        setAlreadySubmitted(!!data.already_submitted)
        const seeded: Record<string, AnswerState> = {}
        for (const it of (data.response.response_items ?? []) as ChecklistResponseItem[]) {
          seeded[it.item_id] = toAnswerState(it)
        }
        setAnswers(seeded)
      } catch (e: any) {
        if (live) setBootErr({ status: e.status ?? 0, message: e.message ?? String(e) })
      }
    })()
    return () => { live = false }
  }, [orgSlug, token])

  const sections: ChecklistSection[] = useMemo(
    () => Array.isArray(boot?.template_snapshot?.sections) ? boot!.template_snapshot.sections : [],
    [boot],
  )
  const allItems: ChecklistItem[] = useMemo(
    () => sections.flatMap((s) => s.items ?? []),
    [sections],
  )

  function setAnswer(itemId: string, patch: Partial<AnswerState>) {
    setAnswers((cur) => ({ ...cur, [itemId]: { ...(cur[itemId] ?? emptyAnswer()), ...patch } }))
  }

  async function persist(itemId: string, next: AnswerState) {
    setUnmet((cur) => { const c = { ...cur }; delete c[itemId]; return c })
    try {
      await fetch(
        `${baseUrl}/api/v1/public/checklist-fill/${encodeURIComponent(orgSlug)}/${encodeURIComponent(token)}/items/${itemId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            value_text: next.value_text,
            value_number: next.value_number,
            value_options: next.value_options,
            notes: next.notes,
            flagged: next.flagged,
          }),
        },
      )
    } catch {
      // Best-effort — the answer stays in local state either way, and
      // submit will surface any question that's genuinely still unmet.
    }
  }

  function answerAndPersist(itemId: string, patch: Partial<AnswerState>) {
    const next = { ...(answers[itemId] ?? emptyAnswer()), ...patch }
    setAnswers((cur) => ({ ...cur, [itemId]: next }))
    persist(itemId, next)
  }

  async function uploadEvidence(itemId: string, file: File, kind: 'image' | 'voice') {
    const form = new FormData()
    form.append('file', file, file.name)
    try {
      const res = await fetch(
        `${baseUrl}/api/v1/public/checklist-fill/${encodeURIComponent(orgSlug)}/${encodeURIComponent(token)}/items/${itemId}/upload-${kind === 'image' ? 'image' : 'voice'}`,
        { method: 'POST', body: form },
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.message ?? `HTTP ${res.status}`)
      setAnswers((cur) => {
        const existing = cur[itemId] ?? emptyAnswer()
        const entry: MediaEntry = { url: data.url, name: data.filename ?? file.name, size: data.size ?? file.size, type: data.contentType ?? file.type }
        return { ...cur, [itemId]: { ...existing, media_urls: [...existing.media_urls, entry] } }
      })
    } catch (e) {
      setSubmitErr(e instanceof Error ? e.message : String(e))
    }
  }

  async function handleSubmit() {
    setSubmitting(true)
    setSubmitErr(null)
    setUnmet({})
    try {
      const res = await fetch(
        `${baseUrl}/api/v1/public/checklist-fill/${encodeURIComponent(orgSlug)}/${encodeURIComponent(token)}/submit`,
        { method: 'POST' },
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        if (res.status === 422 && Array.isArray(data.unmet)) {
          const map: Record<string, string> = {}
          for (const u of data.unmet) map[u.item_id] = u.missing
          setUnmet(map)
          throw new Error(data.message ?? 'Please complete the highlighted questions.')
        }
        throw new Error(data.message ?? `HTTP ${res.status}`)
      }
      setDone(true)
    } catch (e) {
      setSubmitErr(e instanceof Error ? e.message : String(e))
    } finally {
      setSubmitting(false)
    }
  }

  const bg = { minHeight: '100vh', bgcolor: '#F3F4F6', py: { xs: 3, sm: 6 } }

  if (bootErr) {
    return (
      <Box sx={bg}>
        <Container maxWidth="sm">
          <Alert severity={bootErr.status === 410 ? 'warning' : 'error'} icon={<WarningAmberIcon />}>
            {bootErr.status === 410 ? 'This link has expired.' : `Couldn't load this checklist: ${bootErr.message}`}
          </Alert>
        </Container>
      </Box>
    )
  }

  if (!boot) {
    return (
      <Box sx={{ ...bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  const recap = alreadySubmitted || done

  return (
    <Box sx={bg}>
      <Container maxWidth="sm">
        <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }} elevation={0}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'primary.main', mb: 0.5 }}>
            {recap ? 'Checklist submitted' : 'Complete this checklist'}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
            {boot.template_name}
          </Typography>

          {done && (
            <Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ mb: 3, mt: 1 }}>
              Thanks — your answers have been recorded.
            </Alert>
          )}
          {recap && !done && (
            <Typography sx={{ color: 'text.secondary', mb: 3 }}>
              This checklist has already been submitted. Here's what was sent — this page is read-only.
            </Typography>
          )}

          <Stack spacing={3} sx={{ mt: 2 }}>
            {sections.map((section) => (
              <Box key={section.id}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, mb: section.description ? 0.25 : 1 }}>{section.title}</Typography>
                {section.description && (
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 1.5 }}>{section.description}</Typography>
                )}
                <Stack spacing={2}>
                  {(section.items ?? []).map((item) => (
                    <QuestionField
                      key={item.id}
                      item={item}
                      answer={answers[item.id] ?? emptyAnswer()}
                      readOnly={recap}
                      error={unmet[item.id]}
                      onAnswer={(patch) => answerAndPersist(item.id, patch)}
                      onUpload={(file, kind) => uploadEvidence(item.id, file, kind)}
                    />
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>

          {!recap && (
            <>
              {submitErr && <Alert severity="error" sx={{ mt: 3 }}>{submitErr}</Alert>}
              <Button
                variant="contained" size="large" fullWidth
                disabled={submitting}
                onClick={handleSubmit}
                sx={{ mt: 3, py: 1.4, fontWeight: 700 }}
              >
                {submitting ? 'Submitting…' : 'Submit checklist'}
              </Button>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  )
}

function QuestionField({
  item, answer, readOnly, error, onAnswer, onUpload,
}: {
  item: ChecklistItem
  answer: AnswerState
  readOnly: boolean
  error?: string
  onAnswer: (patch: Partial<AnswerState>) => void
  onUpload: (file: File, kind: 'image' | 'voice') => void
}) {
  if (item.type === 'instruction') {
    return (
      <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: 13.5 } }}>
        <strong>{item.label}</strong>
        {item.description && <Box sx={{ mt: 0.25 }}>{item.description}</Box>}
      </Alert>
    )
  }

  const label = `${item.label}${item.required ? ' *' : ''}`
  const errorText = error === 'answer' ? 'This question is required.'
    : error === 'comment' ? 'A comment is required.'
    : error === 'image' ? 'A photo is required.'
    : error === 'voice' ? 'A voice note is required.'
    : undefined

  return (
    <Box>
      {item.description && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>{item.description}</Typography>
      )}
      <AnswerInput item={item} answer={answer} readOnly={readOnly} label={label} onAnswer={onAnswer} />
      {errorText && <Typography sx={{ fontSize: 11.5, color: 'error.main', mt: 0.5 }}>{errorText}</Typography>}

      {item.require_comment && (
        <TextField
          label="Comment" fullWidth size="small" multiline minRows={2} sx={{ mt: 1 }}
          value={answer.notes ?? ''}
          disabled={readOnly}
          onChange={(e) => onAnswer({ notes: e.target.value })}
          error={error === 'comment'}
        />
      )}

      {(item.require_image || item.require_voice) && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          {answer.media_urls.map((m, i) => (
            <EvidenceThumb key={i} media={m} />
          ))}
          {!readOnly && item.require_image && (
            <Button component="label" size="small" variant="outlined" startIcon={<PhotoCameraOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{ fontSize: 12, textTransform: 'none' }}>
              Add photo
              <input type="file" accept="image/*" hidden onChange={(e) => {
                const f = e.target.files?.[0]; if (f) onUpload(f, 'image'); e.target.value = ''
              }} />
            </Button>
          )}
          {!readOnly && item.require_voice && (
            <Button component="label" size="small" variant="outlined" startIcon={<MicNoneOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{ fontSize: 12, textTransform: 'none' }}>
              Add voice note
              <input type="file" accept="audio/*" hidden onChange={(e) => {
                const f = e.target.files?.[0]; if (f) onUpload(f, 'voice'); e.target.value = ''
              }} />
            </Button>
          )}
        </Stack>
      )}
    </Box>
  )
}

function EvidenceThumb({ media }: { media: MediaEntry }) {
  const isImage = media.type.startsWith('image/')
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
      {isImage ? (
        <Box component="img" src={media.url} alt="" sx={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 0.75 }} />
      ) : (
        <MicNoneOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
      )}
      <Typography sx={{ fontSize: 11, maxWidth: 140 }} noWrap>{media.name}</Typography>
    </Box>
  )
}

function AnswerInput({
  item, answer, readOnly, label, onAnswer,
}: {
  item: ChecklistItem
  answer: AnswerState
  readOnly: boolean
  label: string
  onAnswer: (patch: Partial<AnswerState>) => void
}) {
  const type: ChecklistItemType = item.type

  if (type === 'text') {
    return (
      <TextField
        label={label} fullWidth size="small" multiline minRows={2}
        value={answer.value_text ?? ''} disabled={readOnly}
        onChange={(e) => onAnswer({ value_text: e.target.value })}
      />
    )
  }
  if (type === 'number') {
    return (
      <TextField
        label={label} type="number" fullWidth size="small"
        value={answer.value_number ?? ''} disabled={readOnly}
        inputProps={{ min: item.validation?.min, max: item.validation?.max }}
        onChange={(e) => onAnswer({ value_number: e.target.value === '' ? null : Number(e.target.value) })}
      />
    )
  }
  if (type === 'date') {
    return (
      <TextField
        label={label} type="date" fullWidth size="small" InputLabelProps={{ shrink: true }}
        value={answer.value_text ?? ''} disabled={readOnly}
        onChange={(e) => onAnswer({ value_text: e.target.value })}
      />
    )
  }
  if (type === 'yes_no') {
    return (
      <Stack spacing={0.75}>
        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{label}</Typography>
        <ToggleButtonGroup
          exclusive size="small" value={answer.value_text}
          onChange={(_, v) => v && onAnswer({ value_text: v })}
          disabled={readOnly}
        >
          <ToggleButton value="yes" sx={{ fontSize: 12.5, textTransform: 'none', px: 2 }}>Yes</ToggleButton>
          <ToggleButton value="no" sx={{ fontSize: 12.5, textTransform: 'none', px: 2 }}>No</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
    )
  }
  if (type === 'pass_fail') {
    return (
      <Stack spacing={0.75}>
        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{label}</Typography>
        <ToggleButtonGroup
          exclusive size="small" value={answer.value_text}
          onChange={(_, v) => v && onAnswer({ value_text: v })}
          disabled={readOnly}
        >
          <ToggleButton value="pass" color="success" sx={{ fontSize: 12.5, textTransform: 'none', px: 2 }}>Pass</ToggleButton>
          <ToggleButton value="fail" color="error" sx={{ fontSize: 12.5, textTransform: 'none', px: 2 }}>Fail</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
    )
  }
  if (type === 'rating') {
    return (
      <Stack spacing={0.5}>
        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{label}</Typography>
        <Rating
          value={answer.value_number ?? 0} disabled={readOnly}
          onChange={(_, v) => onAnswer({ value_number: v })}
        />
      </Stack>
    )
  }
  if (type === 'multiple_choice') {
    const options = item.options ?? []
    const selected = answer.value_options?.[0] ?? null
    return (
      <TextField
        select label={label} fullWidth size="small"
        value={selected ?? ''} disabled={readOnly}
        onChange={(e) => onAnswer({ value_options: e.target.value ? [e.target.value] : null })}
      >
        <MenuItem value="" disabled>Select an option…</MenuItem>
        {options.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
      </TextField>
    )
  }
  if (type === 'checkbox') {
    const options = item.options ?? []
    const selected = new Set(answer.value_options ?? [])
    return (
      <Stack spacing={0.5}>
        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{label}</Typography>
        <Stack spacing={0.5}>
          {options.map((o) => (
            <Chip
              key={o}
              label={o}
              clickable={!readOnly}
              color={selected.has(o) ? 'primary' : 'default'}
              variant={selected.has(o) ? 'filled' : 'outlined'}
              onClick={() => {
                if (readOnly) return
                const next = new Set(selected)
                next.has(o) ? next.delete(o) : next.add(o)
                onAnswer({ value_options: Array.from(next) })
              }}
              sx={{ fontSize: 12.5, alignSelf: 'flex-start' }}
            />
          ))}
        </Stack>
      </Stack>
    )
  }
  return null
}
